import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronDownIcon } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Form,
  FormField,
  Button,
  useZodForm,
  useModalControls,
  type FieldOption,
  type MultiSelectOption,
} from '@noor/ui';
import { getApiErrorMessage, getApiFieldErrors } from '@noor/api';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { cn } from '@noor/lib/cn';
import { usePromotionDetail } from '@/entities/promotion';
import {
  buildPromotionFormSchema,
  selectedProductIds,
  PROMOTION_NAME_MAX_LENGTH,
  type PromotionFormValues,
} from '../model/schema';
import { useUpsertPromotion } from '../model/use-upsert-promotion';
import {
  buildDefaults,
  buildProductDescription,
  formatDateLabel,
  parseIsoDate,
  formatDuration,
  fixedPriceCeiling,
  toUpsertVars,
} from '../lib/promotion-form';
import { MobileOptionPicker } from './mobile-option-picker';

/** Filled gray pill field — the mobile design's one input treatment. */
const FIELD = 'h-12 rounded-3xl border-none bg-muted shadow-none';

/** Figma label: 14px medium, gray-700, 4px inset, 8px above its control. */
const GROUP = 'gap-2';
const LABEL = 'px-1 text-sm font-medium tracking-tight text-secondary-foreground';

/** Validation text that takes up no room until there is something to say. */
const MESSAGE = 'min-h-0 px-1 empty:hidden';

/** Which full-screen picker is open over the sheet, if any. */
type PickerField = 'branchIds' | 'productIds' | null;

type PromotionModalMobileProps =
  | ModalRenderProps<'add-promotion'>
  | ModalRenderProps<'edit-promotion'>;

/**
 * Create / edit promotion on mobile (Figma node 26280:94801) — a bottom sheet
 * over the promotions screen, not the desktop dialog rendered narrow.
 *
 * Rendered through the bare `Dialog` primitives rather than the shared `Modal`
 * shell: that shell centers a card with a close-X, while this design is a
 * sheet anchored to the bottom edge with a drag indicator above it and no
 * close-X at all — dismissal is the backdrop or a swipe.
 *
 * The two select fields do NOT use `MultiSelect`: below its 1040px threshold
 * it degrades to an anchored dropdown, and the design gives each one a
 * full-screen picker instead (`MobileOptionPicker`, Figma 26280:95043). Every
 * other field is the shared `FormField` control in the design's filled-pill
 * treatment.
 *
 * Schema, defaults, mappers and the upsert mutation are all shared with the
 * desktop modal — only the view differs (CLAUDE.md §6c).
 */
export function PromotionModalMobile({ payload, close }: PromotionModalMobileProps) {
  const { t } = useTranslation('eats');
  const { open } = useModalControls();
  const upsert = useUpsertPromotion();
  const [picker, setPicker] = useState<PickerField>(null);

  const isEditing = payload.mode === 'edit';

  const branchOptions = useMemo<MultiSelectOption[]>(
    () => payload.branches.map(({ id, name }) => ({ label: name, value: id })),
    [payload.branches],
  );

  // Categories are only carried on the add payload; the edit payload keeps the
  // product list alone, so the subtitle falls back to the price there.
  const categoryNames = useMemo(() => {
    const entries =
      payload.mode === 'add'
        ? payload.categories.map((c) => [String(c.id), c.name] as const)
        : [];
    return new Map(entries);
  }, [payload]);

  const productOptions = useMemo<MultiSelectOption[]>(
    () =>
      payload.products.map((product) => ({
        label: product.name,
        value: String(product.id),
        description: buildProductDescription(
          categoryNames.get(String(product.categoryId)),
          product.price,
          t('promotions.form.currencySuffix'),
        ),
      })),
    [payload.products, categoryNames, t],
  );

  const discountTypeOptions = useMemo<FieldOption[]>(
    () => [
      { label: t('promotions.form.discountType.percent'), value: 'percent' },
      { label: t('promotions.form.discountType.fixed'), value: 'fixed' },
    ],
    [t],
  );

  // The table card this modal is opened with is a joined projection naming its
  // product and branch by DISPLAY NAME, so seeding from it alone matches those
  // back by string. Re-read the promotion itself for the ids (CLAUDE.md §8).
  // The by-id read is BRANCH-SCOPED now, so it is read for the row's own
  // branch — the one this card was opened from, and the only branch this sheet
  // can meaningfully diff against on save.
  const { data: detail, dataUpdatedAt } = usePromotionDetail(
    payload.mode === 'edit' ? String(payload.promotion.id) : undefined,
    payload.mode === 'edit' ? payload.promotion.restaurantId : undefined,
  );

  const schema = useMemo(() => buildPromotionFormSchema(t), [t]);
  const form = useZodForm(schema, {
    defaultValues: buildDefaults(payload, detail),
    mode: 'onSubmit',
  });

  const { reset } = form;
  useFormSeed(
    true,
    payload.mode === 'edit' ? String(payload.promotion.id) : undefined,
    dataUpdatedAt,
    () => reset(buildDefaults(payload, detail)),
  );

  const discountType = form.watch('discountType');
  const isPercent = discountType === 'percent';
  const startDate = form.watch('startDate');
  /**
   * The sheet's two flat pickers, bridged onto the form's per-branch
   * `selections` map.
   *
   * The DESKTOP wizard collects products per branch (a branch can carry its own
   * set), which is what the wire's `selections` body wants. This sheet keeps
   * its two independent pickers — a phone has no room for the wizard's
   * side-by-side branch/product panes — so it expresses the simpler case: every
   * chosen product on every chosen branch. Reading is the union of the map;
   * writing rebuilds it as that cross product.
   */
  const selections = form.watch('selections') ?? {};
  const branchIds = Object.keys(selections);
  const productIds = selectedProductIds(selections);

  function setSelection(nextBranchIds: string[], nextProductIds: string[]): void {
    form.setValue(
      'selections',
      Object.fromEntries(nextBranchIds.map((id) => [id, nextProductIds])),
      { shouldValidate: true, shouldDirty: true },
    );
  }

  /**
   * Floor for the promo window: midnight today, so "today" itself stays
   * pickable while every earlier day greys out. An EDIT floors at the start it
   * was SAVED with when that is earlier — a promo running since last month is
   * a legitimate record, and flooring it at today would grey out its own start
   * date and make the row unsaveable without silently moving it.
   */
  const earliestStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const saved = detail ? parseIsoDate(detail.startsAt) : undefined;
    return saved && saved < today ? saved : today;
  }, [detail]);

  function handleSubmit(values: PromotionFormValues): void {
    const ceiling = fixedPriceCeiling(values, payload, undefined);
    if (ceiling !== undefined) {
      form.setError('discountValue', {
        type: 'manual',
        message: t('promotions.form.fixedPriceTooHigh'),
      });
      return;
    }
    const duration = formatDuration(values.startDate, values.endDate);

    // `detail` names the products the campaign currently covers — the diff
    // against the form's selection is what tells an unchecked product from
    // one that was never on it.
    // The sheet reads the detail for the ROW's own branch, so that is the
    // branch the removal diff is scoped to. Passed explicitly — the response's
    // `restaurant_id` comes back `''` and cannot be used for this.
    upsert.mutate(
      toUpsertVars(
        values,
        payload,
        duration,
        detail,
        undefined,
        payload.mode === 'edit' ? payload.promotion.restaurantId : undefined,
      ),
      {
        onSuccess: (outcome) => {
          // A gated merchant's write is QUEUED for moderation rather than
          // applied, so the queued case says so instead of closing silently.
          if (outcome.status === 'pending') {
            toast.success(t('promotions.form.pendingReview'));
          }
          close();
        },
        // Surface the server's own reason — a rejected promotion write is
        // usually a specific rule (an overlapping campaign, a branch that
        // refuses the discount), and a generic failure hides which.
        onError: (error) => {
          const { reason } = getApiFieldErrors(error);
          toast.error(getApiErrorMessage(error, reason ?? t('promotions.form.error')));
        },
      },
    );
  }

  const title = t(isEditing ? 'common:modal.edition' : 'common:modal.creation');

  const branchSummary = summarize(branchIds, branchOptions);
  const productSummary = summarize(productIds, productOptions);

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
            )}
          >
            {/* The design's drag indicator, floating clear of the card. */}
            <div className="flex shrink-0 justify-center pb-2">
              <div className="h-1 w-10 rounded-sm bg-muted" />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-8 rounded-t-[2rem] bg-card p-4">
              <DialogTitle className="px-1 text-[1.375rem] leading-none font-semibold tracking-tight text-foreground">
                {title}
              </DialogTitle>
              <DialogDescription className="sr-only">{title}</DialogDescription>

              <Form
                form={form}
                onSubmit={handleSubmit}
                className="flex min-h-0 flex-1 flex-col gap-8"
              >
                {/* `-mx-1 px-1`: a focus ring is a 3px box-shadow drawn
                 * OUTSIDE the field's border box, and `overflow-y-auto` clips
                 * the cross axis too — so a full-width field's ring is cut off
                 * flat at both edges without room to render into. The negative
                 * margin keeps the fields flush with the sheet's gutter. */}
                <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1">
                  {/* The campaign's own name — required by the schema, so the
                   * sheet has to collect it or every mobile submit fails
                   * validation on a field the merchant cannot see. Ru ONLY:
                   * three stacked name inputs would dominate a phone sheet, and
                   * Ru is the one the schema requires (uz/en are genuinely
                   * optional, and the desktop wizard is where a merchant fills
                   * them in). A blank uz/en goes as `''`, which the backend
                   * reads as "not provided" and leaves alone — so editing a
                   * translated campaign here never wipes its translations. */}
                  <FormField name="nameRu" className={GROUP}>
                    <FormField.Label className={LABEL}>
                      {t('promotions.form.nameLabel')}
                      <span className="text-primary">*</span>
                    </FormField.Label>
                    <FormField.Input
                      className={FIELD}
                      maxLength={PROMOTION_NAME_MAX_LENGTH}
                      placeholder={t('promotions.form.namePlaceholder')}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>

                  {/* Both pickers write into the SAME `selections` field —
                   * there is no `branchIds`/`productIds` in the schema any
                   * more, and naming fields that do not exist bound these to
                   * nothing: RHF reported no error and the required rule was
                   * invisible here. Only the product picker renders the
                   * message, so the one rule is not stated twice. */}
                  <FormField name="selections" className={GROUP}>
                    <FormField.Label className={LABEL}>
                      {t('promotions.form.branchesLabel')}
                    </FormField.Label>
                    <PickerTrigger
                      onClick={() => setPicker('branchIds')}
                      value={branchSummary}
                      placeholder={t('promotions.form.branchesPlaceholder')}
                    />
                  </FormField>

                  <FormField name="selections" className={GROUP}>
                    <FormField.Label className={LABEL}>
                      {t('promotions.form.productsLabel')}
                    </FormField.Label>
                    <PickerTrigger
                      onClick={() => setPicker('productIds')}
                      value={productSummary}
                      placeholder={t('promotions.form.productsPlaceholder')}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>

                  <FormField name="discountType" className={GROUP}>
                    <FormField.Label className={LABEL}>
                      {t('promotions.form.discountTypeLabel')}
                    </FormField.Label>
                    <FormField.Select className={FIELD} options={discountTypeOptions} />
                    <FormField.Message className={MESSAGE} />
                  </FormField>

                  <FormField name="discountValue" className={GROUP}>
                    <FormField.Label className={LABEL}>
                      {isPercent
                        ? t('promotions.form.percentLabel')
                        : t('promotions.form.fixedPriceLabel')}
                    </FormField.Label>
                    <FormField.Price
                      className={FIELD}
                      {...(isPercent ? { maxValue: 100 } : {})}
                      placeholder={
                        isPercent
                          ? t('promotions.form.percentPlaceholder')
                          : t('promotions.form.pricePlaceholder')
                      }
                      suffix={
                        isPercent
                          ? t('promotions.form.percentSuffix')
                          : t('promotions.form.currencySuffix')
                      }
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>

                  {/* Every promotion is dated — both ends are required. */}
                  <div className="flex items-start gap-2">
                    <FormField name="startDate" className={`flex-1 ${GROUP}`}>
                      <FormField.Label className={LABEL}>
                        {t('promotions.form.startLabel')}
                      </FormField.Label>
                      {/* A promo window cannot open in the past; greying
                            those days out states the rule where it applies. */}
                      <FormField.DatePicker
                        hideIcon
                        formatDate={formatDateLabel}
                        className={FIELD}
                        placeholder={t('promotions.form.datePlaceholder')}
                        disabledDates={{ before: earliestStart }}
                      />
                      <FormField.Message className={MESSAGE} />
                    </FormField>

                    <FormField name="endDate" className={`flex-1 ${GROUP}`}>
                      <FormField.Label className={LABEL}>
                        {t('promotions.form.endLabel')}
                      </FormField.Label>
                      {/* The end can never precede the start, so the floor
                            moves with it. */}
                      <FormField.DatePicker
                        hideIcon
                        formatDate={formatDateLabel}
                        className={FIELD}
                        placeholder={t('promotions.form.datePlaceholder')}
                        disabledDates={{ before: startDate ?? earliestStart }}
                      />
                      <FormField.Message className={MESSAGE} />
                    </FormField>
                  </div>

                  <Form.RootError />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="h-14 w-full shrink-0 rounded-full text-base"
                  disabled={upsert.isPending}
                >
                  {t('common:actions.save')}
                </Button>
              </Form>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* The picker is mounted beside the sheet rather than inside it —
       * nesting a full-screen dialog in the sheet's content would put it
       * inside the sheet's own scroll container. Mounted only while open, so
       * each open seeds its local selection fresh from the field (the picker
       * takes `value` as its initial state and has no re-seeding effect). */}
      {picker !== null && (
        <MobileOptionPicker
          open
          onOpenChange={(next) => (next ? undefined : setPicker(null))}
          title={
            picker === 'branchIds'
              ? t('promotions.form.branchesLabel')
              : t('promotions.form.productsLabel')
          }
          options={picker === 'branchIds' ? branchOptions : productOptions}
          value={picker === 'branchIds' ? branchIds : productIds}
          onConfirm={(next) =>
            picker === 'branchIds'
              ? setSelection([...next], productIds)
              : setSelection(branchIds, [...next])
          }
          confirmLabel={t('promotions.form.pickerConfirm')}
          closeLabel={t('utilities.close')}
          emptyText={t('promotions.form.searchEmpty')}
        />
      )}
    </>
  );
}

/**
 * The filled pill that stands in for a select: it shows the current selection
 * and opens the full-screen picker instead of a dropdown, so it is a plain
 * button rather than a `FormField` control.
 */
function PickerTrigger({
  onClick,
  value,
  placeholder,
}: {
  onClick: () => void;
  value: string | undefined;
  placeholder: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-12 w-full cursor-pointer items-center gap-2 rounded-3xl bg-muted px-4 py-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
    >
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm tracking-tight',
          value ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {value ?? placeholder}
      </span>
      <ChevronDownIcon className="size-5 shrink-0 text-muted-foreground" />
    </button>
  );
}

/** Trigger text for a selection: the single name, or `«name» +N`. */
function summarize(selected: string[], options: MultiSelectOption[]): string | undefined {
  if (selected.length === 0) return undefined;
  const [first] = selected;
  const label = options.find((option) => option.value === first)?.label;
  if (label === undefined) return undefined;
  return selected.length > 1 ? `${label} +${selected.length - 1}` : label;
}
